import React, { useCallback, useMemo } from 'react'

import { bool, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import { PLAN_TYPE, TIER } from '../../constants'

const FEE_FREE_KEY = 'fee_free.consequences'

const AI_CONSEQUENCE_KEYS = [
  'keep_access',
  'no_generations',
  'keep_license_ai',
  'no_fee_no_commitment',
]

function ConsequencesList({
  hasFee = false,
  planType = 'yearly',
  gracePeriodEligible = true,
  commitmentReleased = false,
  isFeeFreeFlow = false,
  cancelEndsNow = false,
  isAnnual = false,
  accessUntilFormatted = null,
  locale = 'en',
  tier = TIER.ESSENTIALS,
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(
      `subscription_management.cancel_modal.${key}`,
      { ns: 'rails', lng: locale, ...options }
    ),
    [locale]
  )

  // Fee terms differ per cohort: legacy (grace-period) subscribers keep the conditional
  // 45-day promise; the v1.6 yearly cohort has the same 45-day window plus a zero-downloads
  // waiver, so its terms are only stated when a fee is actually due (the no-fee banner
  // already covers the waived case); monthly plans and released subscriptions never carry a fee.
  const feeNoticeKey = useMemo(() => {
    if (planType === PLAN_TYPE.MONTHLY || commitmentReleased) return null
    if (gracePeriodEligible) return 'fee_notice'

    return hasFee ? 'fee_notice_new_yearly' : null
  }, [planType, commitmentReleased, gracePeriodEligible, hasFee])

  const feeFreeItems = () => {
    const plan = I18n.t(`subscription_management.plan_change.tiers.${tier}`, { ns: 'rails', lng: locale })
    let accessItem = translate('consequences.keep_access')
    // Draft copy pending Artem's review (124g39pdhdx).
    if (cancelEndsNow) accessItem = translate(`${FEE_FREE_KEY}.ends_now`)
    else if (accessUntilFormatted) {
      accessItem = translate(`${FEE_FREE_KEY}.keep_access_${isAnnual ? 'year' : 'month'}`, {
        plan, date: accessUntilFormatted,
      })
    }

    return [
      accessItem,
      ...(cancelEndsNow ? [] : [translate(`${FEE_FREE_KEY}.no_access_after`)]),
      translate(`${FEE_FREE_KEY}.keep_license`),
      ...(isAnnual ? [translate(`${FEE_FREE_KEY}.no_refund_annual`)] : []),
    ]
  }

  const legacyKeys = tier === TIER.AI_STUDIO
    ? AI_CONSEQUENCE_KEYS
    : [
      'keep_access',
      'no_downloads',
      'keep_license',
      ...(feeNoticeKey ? [feeNoticeKey] : []),
    ]
  const items = isFeeFreeFlow
    ? feeFreeItems()
    : legacyKeys.map((key) => translate(`consequences.${key}`))

  return (
    <div className="cancel-subscription-modal__consequences">
      <h4 className="cancel-subscription-modal__consequences-title">
        {translate(isFeeFreeFlow ? `${FEE_FREE_KEY}.title` : 'consequences_title')}
      </h4>
      <ul className="cancel-subscription-modal__consequences-list">
        {items.map((item) => (
          <li
            key={item}
            className="cancel-subscription-modal__consequences-item cgt-text-medium"
          >
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

ConsequencesList.propTypes = {
  hasFee: bool,
  planType: string,
  gracePeriodEligible: bool,
  commitmentReleased: bool,
  isFeeFreeFlow: bool,
  cancelEndsNow: bool,
  isAnnual: bool,
  accessUntilFormatted: string,
  locale: string,
  tier: string,
}

export default ConsequencesList
